/**
 * Logger utility for consistent logging across the application
 * Automatically disables debug logs in production
 */

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

const isDevelopment = import.meta.env.DEV;
const isProduction = import.meta.env.PROD;

class Logger {
  private formatMessage(level: LogLevel, message: string, ...args: unknown[]): string {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;
    return `${prefix} ${message}`;
  }

  private shouldLog(level: LogLevel): boolean {
    // Always log errors and warnings
    if (level === 'error' || level === 'warn') {
      return true;
    }
    // Only log debug/info in development
    return isDevelopment;
  }

  debug(message: string, ...args: unknown[]): void {
    if (this.shouldLog('debug')) {
      console.log(this.formatMessage('debug', message), ...args);
    }
  }

  info(message: string, ...args: unknown[]): void {
    if (this.shouldLog('info')) {
      console.info(this.formatMessage('info', message), ...args);
    }
  }

  warn(message: string, ...args: unknown[]): void {
    if (this.shouldLog('warn')) {
      console.warn(this.formatMessage('warn', message), ...args);
    }
  }

  error(message: string, error?: Error | unknown, ...args: unknown[]): void {
    // Always log errors, even in production
    const errorDetails = error instanceof Error 
      ? { message: error.message, stack: error.stack }
      : error;
    
    console.error(
      this.formatMessage('error', message),
      errorDetails ? errorDetails : '',
      ...args
    );

    // In production, you might want to send errors to an error tracking service
    if (isProduction && error instanceof Error) {
      // TODO: Integrate with error tracking service (e.g., Sentry)
      // Example: Sentry.captureException(error, { extra: { message, ...args } });
    }
  }

  // Specialized loggers for common use cases
  apiRequest(method: string, url: string, ...args: unknown[]): void {
    this.debug(`[API Request] ${method} ${url}`, ...args);
  }

  apiResponse(status: number, statusText: string, url: string): void {
    if (status >= 400) {
      this.error(`[API Response] ${status} ${statusText} - ${url}`);
    } else {
      this.debug(`[API Response] ${status} ${statusText} - ${url}`);
    }
  }

  apiError(message: string, error: unknown, url?: string): void {
    this.error(`[API Error] ${message}${url ? ` - ${url}` : ''}`, error);
  }
}

// Export singleton instance
export const logger = new Logger();

// Export type for convenience
export type { LogLevel };

